
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useScrollAnimation, useStaggeredAnimation } from '@/hooks/useScrollAnimation';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '-10% 0px' });
  const faqsAnimation = useScrollAnimation();

  const faqs = [
    {
      question: "Como funciona o acesso às salas ao vivo?",
      answer: "Após a confirmação do pagamento, você recebe imediatamente acesso à nossa plataforma exclusiva. As salas funcionam de segunda a sexta, das 8h30 às 12h00, com transmissões ao vivo feita por profissional certificado. Você pode acompanhar pelo computador, tablet ou celular."
    },
    {
      question: "Posso cancelar minha assinatura a qualquer momento?",
      answer: "Sim! Você pode cancelar sua assinatura a qualquer momento sem burocracias. Dentro do período de garantia de cada plano, se não ficar satisfeito, basta solicitar e devolvemos 100% do valor investido na assinatura do plano."
    },
    {
      question: "Qual é o horário de funcionamento das salas?",
      answer: "Nossa sala funciona nos dias que ocorrem pregão na bolsa brasileira: Segunda a sexta das 8h30 às 12h00."
    },
    {
      question: "Preciso ter experiência prévia em trading?",
      answer: "Não é necessário! Nossas salas atendem desde iniciantes até traders experientes. Oferecemos conteúdo educativo básico para quem está começando e análises avançadas para traders mais experientes."
    },
    {
      question: "Quanto dinheiro preciso para começar a operar?",
      answer: "Recomendamos começar com pelo menos R$ 1.000 para Day Trade. O importante é nunca investir dinheiro que você não pode perder. Nossos analistas sempre enfatizam o gerenciamento de risco e as boas práticas de trading."
    },
    {
      question: "Os resultados mostrados são reais?",
      answer: "Sim! Todos os resultados apresentados são verificáveis e auditados."
    },
    {
      question: "O Analista dirá quando eu devo entrar e sair manualmente da operação?",
      answer: "Não! Pois nossos analistas operam exclusivamente utilizando nossos robôs, de forma que todas as orientações do analista será baseado em operações a serem realizadas utilizando robôs. Desta forma você elimita o fator emocional durante a operação em andamento."
    },
    {
      question: "Existe suporte técnico disponível?",
      answer: "Sim! Oferecemos suporte técnico completo de segunda a sexta das 8h às 18h. Nossa equipe está disponível via WhatsApp."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-slate-800/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-500 will-change-transform ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-2'
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Dúvidas <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">Frequentes</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Tire suas principais dúvidas sobre nossa plataforma e como começar a operar com nossos especialistas
          </p>
        </div>

        <div 
          ref={faqsAnimation.ref}
          className="space-y-4"
        >
          {faqs.map((faq, index) => (
            <Card 
              key={index} 
              className={`bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all duration-500 scroll-animate stagger-${index + 1} ${faqsAnimation.isVisible ? 'visible' : ''}`}
            >
              <CardContent className="p-0">
                <button
                  onClick={() => toggleFAQ(index)}
                  aria-expanded={openFAQ === index}
                  aria-controls={`faq-content-${index}`}
                  className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-slate-700/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-800"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0">
                    {openFAQ === index ? (
                      <ChevronUp className="h-5 w-5 text-cyan-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </button>
                
                {openFAQ === index && (
                  <div id={`faq-content-${index}`} className="px-6 pb-5">
                    <div className="pt-4 border-t border-slate-700">
                      <p className="text-slate-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Still have questions section */}
        <div id="contact" className="mt-16 text-center">
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 rounded-2xl p-8 border border-slate-600">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ainda tem dúvidas?
            </h3>
            <p className="text-slate-300 mb-6">
              Nossa equipe está pronta para esclarecer qualquer questão sobre nossas salas e metodologias de trading automatizado utilizando nossos robôs traders.
            </p>
            <div className="flex justify-center">
              <a 
                href={`https://wa.me/5511948621950?text=${encodeURIComponent("Olá! Tenho interesse em saber como contratar os produtos da *QUANTMAKE*!")}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Entrar em contato via WhatsApp com suporte QuantMake"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-cyan-400 to-green-400 hover:from-cyan-500 hover:to-green-500 text-slate-900 font-semibold rounded-full transition-all duration-200 shadow-lg hover:shadow-cyan-400/25 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-800"
              >
                WhatsApp Suporte
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
